/**
 * Build Your Box — reusable purchase module.
 * Discount source of truth is Appstle (see developers.appstle.com/bundles/headless-overview):
 * pack/volume discount is authored in Appstle Bundles, published to the
 * `appstle_bundles.bundle_rules` shop metafield, and applied by Appstle's Shopify Function
 * when cart lines carry `_appstle-bb-id` / `_appstle_bundles_type`. Previews below are computed
 * from that same rule. Subscribe = Appstle Subscriptions selling-plan allocation (`selling_plan`).
 * Theme tier blocks are only a fallback when no ACTIVE Appstle rule exists.
 */
(function () {
  if (window.__byobLoaded) return;
  window.__byobLoaded = true;

  function money(cents, currency) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency || 'USD' }).format((cents || 0) / 100);
  }

  function init(root) {
    if (!root || root.dataset.byobInit) return;
    var configEl = root.querySelector('[data-byob-config]');
    if (!configEl) return;
    var cfg;
    try { cfg = JSON.parse(configEl.textContent); } catch (e) { return; }
    if (!cfg.tiers.length || !cfg.flavors.length) return;
    root.dataset.byobInit = '1';

    function safeParse(v) {
      if (typeof v === 'string') { try { return JSON.parse(v); } catch (e) { return null; } }
      return v;
    }
    var rules = safeParse(cfg.bundleRules) || [];
    if (!Array.isArray(rules)) rules = rules.bundle_rules || rules.rules || [];
    var supportedTypes = ['VOLUME_DISCOUNT', 'CLASSIC_BUILD_A_BOX', 'SINGLE_PRODUCT_BUILD_A_BOX'];
    var rule = null;
    (rules || []).forEach(function (r) {
      if (!r || String(r.status).toUpperCase() !== 'ACTIVE' || supportedTypes.indexOf(r.bundleType) === -1) return;
      if (cfg.bundleUniqueRef) { if (r.uniqueRef === cfg.bundleUniqueRef) rule = r; }
      else if (!rule) rule = r;
    });
    if (cfg.bundleUniqueRef && !rule) console.warn('[BYOB] Appstle bundle rule not found for uniqueRef:', cfg.bundleUniqueRef);
    if (!rule) console.info('[BYOB] No ACTIVE Appstle Bundles rule found; using theme tier blocks as fallback.');

    var themeTiers = cfg.tiers.slice().sort(function (a, b) { return a.packs - b.packs; });
    function themeTierFor(packs) { return themeTiers.find(function (x) { return x.packs === packs; }) || {}; }
    var tiers = themeTiers;
    if (rule && rule.bundleType === 'VOLUME_DISCOUNT') {
      var parsed = (safeParse(rule.tieredDiscount) || []).filter(function (x) {
        return !x.discountBasedOn || String(x.discountBasedOn).toUpperCase() === 'QUANTITY';
      }).sort(function (a, b) { return a.value - b.value; });
      if (parsed.length) {
        var maxPacks = parsed[parsed.length - 1].value;
        tiers = parsed.map(function (x) {
          var pct = String(x.discountType).toUpperCase() === 'PERCENTAGE' ? Number(x.discount) || 0 : 0;
          // Appstle volume rule is the source of truth for both one-time and subscribe
          // previews (Sunkist selling plan is 0%; the Function applies this % at checkout).
          return {
            packs: Number(x.value),
            oneTimeDiscount: pct,
            fixedAmount: pct ? 0 : Number(x.discount) || 0,
            subscriptionDiscount: pct,
            isDefault: Number(x.value) === maxPacks
          };
        });
      }
    } else if (rule && rule.bundleType === 'CLASSIC_BUILD_A_BOX') {
      var minP = parseInt(rule.minProductCount, 10) || 1;
      var maxP = parseInt(rule.maxProductCount, 10) || themeTiers[themeTiers.length - 1].packs;
      var classicPct = String(rule.discountType).toUpperCase() === 'PERCENTAGE' ? Number(rule.discountValue) || 0 : 0;
      tiers = [];
      for (var n = minP; n <= maxP; n++) {
        tiers.push({
          packs: n,
          oneTimeDiscount: classicPct,
          fixedAmount: classicPct ? 0 : Number(rule.discountValue) || 0,
          subscriptionDiscount: themeTierFor(n).subscriptionDiscount || classicPct,
          isDefault: n === maxP
        });
      }
    }
    var defaultTier = tiers.findIndex(function (t) { return t.isDefault; });
    if (defaultTier < 0) defaultTier = tiers.length - 1;

    var state = {
      tierIndex: defaultTier,
      slots: [], // flavor indexes, length = selected tier packs
      purchase: cfg.defaultPurchase === 'one_time' ? 'one_time' : 'subscribe',
      loading: false
    };
    function resetSlots() {
      state.slots = new Array(tiers[state.tierIndex].packs).fill(null);
    }
    resetSlots();

    var els = {
      tiers: Array.prototype.slice.call(root.querySelectorAll('[data-byob-tier]')),
      slots: root.querySelector('[data-byob-slots]'),
      flavors: Array.prototype.slice.call(root.querySelectorAll('[data-byob-flavor]')),
      progressLabel: root.querySelector('[data-byob-progress-label]'),
      progressBar: root.querySelector('[data-byob-progress-bar]'),
      subPrice: root.querySelector('[data-byob-sub-price]'),
      subCompare: root.querySelector('[data-byob-sub-compare]'),
      subPer: root.querySelector('[data-byob-sub-per]'),
      otPrice: root.querySelector('[data-byob-ot-price]'),
      otPer: root.querySelector('[data-byob-ot-per]'),
      giftStrip: root.querySelector('[data-byob-gift-strip]'),
      subBadge: root.querySelector('[data-byob-sub-badge]'),
      atc: root.querySelector('[data-byob-atc]'),
      atcPrice: root.querySelector('[data-byob-atc-price]'),
      error: root.querySelector('[data-byob-error]'),
      builder: root.querySelector('[data-byob-builder]'),
      buyBtns: Array.prototype.slice.call(root.querySelectorAll('[data-byob-buy]'))
    };

    function selectedPacks() { return tiers[state.tierIndex].packs; }
    function isSinglePack() { return selectedPacks() < 2; }
    function pageFlavorIndex() {
      if (!cfg.pageProductId) return -1;
      return cfg.flavors.findIndex(function (f) {
        return String(f.productId) === String(cfg.pageProductId);
      });
    }

    function filled() { return state.slots.filter(function (i) { return i !== null; }); }
    function tierFor(count) {
      // Effective tier = tier whose packs == filled count; fallback nearest lower/higher.
      var match = tiers.find(function (t) { return t.packs === count; });
      if (match) return match;
      var lower = tiers.filter(function (t) { return t.packs < count; }).pop();
      return lower || tiers[0];
    }
    function refPrice() {
      var first = cfg.flavors.find(function (f) { return f.available; }) || cfg.flavors[0];
      return first ? first.price : 0;
    }
    function pickAllocation(flavor, targetDiscount) {
      var list = (flavor.allocations || []).filter(function (a) {
        return !cfg.sellingPlanNameContains || a.name.toLowerCase().indexOf(String(cfg.sellingPlanNameContains).toLowerCase()) > -1;
      });
      if (!list.length) list = flavor.allocations || [];
      if (!list.length) return null;
      return list.slice().sort(function (a, b) {
        var da = Math.abs((flavor.price ? (1 - a.price / flavor.price) * 100 : 0) - targetDiscount);
        var db = Math.abs((flavor.price ? (1 - b.price / flavor.price) * 100 : 0) - targetDiscount);
        return da - db;
      })[0];
    }
    // Match Shopify Function rounding: discount each line, then sum (avoids 1¢ PDP/cart drift).
    function discountedLineTotal(priceCents, pct) {
      if (!pct) return priceCents;
      var discount = Math.round(priceCents * pct / 100);
      return Math.max(priceCents - discount, 0);
    }
    function discountedLinesTotal(prices, pct) {
      return prices.reduce(function (sum, p) { return sum + discountedLineTotal(p, pct); }, 0);
    }
    function totals() {
      var items = filled().map(function (i) { return cfg.flavors[i]; });
      var count = items.length;
      var tier = tierFor(count);
      var base = items.reduce(function (sum, f) { return sum + f.price; }, 0);
      var prices = items.map(function (f) { return f.price; });
      var oneTime = count && tier.fixedAmount
        ? Math.max(base - Math.round(tier.fixedAmount * 100), 0)
        : discountedLinesTotal(prices, count ? tier.oneTimeDiscount : 0);
      var sub = 0, subBase = 0, allocations = [];
      var allocSum = 0;
      items.forEach(function (f) {
        var a = pickAllocation(f, tier.subscriptionDiscount);
        allocations.push(a);
        allocSum += a ? a.price : f.price;
        subBase += a && a.compareAtPrice ? a.compareAtPrice : f.price;
      });
      // Display price: if the selling plan itself already prices at (about) the tier
      // discount, allocation prices are authoritative. Otherwise the Appstle Function
      // applies the tier discount at checkout, so preview that tier price instead of
      // the undiscounted allocation sum.
      var allocPct = base ? (1 - allocSum / base) * 100 : 0;
      var tierSub = tier.fixedAmount
        ? Math.max(base - Math.round(tier.fixedAmount * 100), 0)
        : discountedLinesTotal(prices, count ? tier.subscriptionDiscount : 0);
      sub = count && Math.abs(allocPct - tier.subscriptionDiscount) >= 1.5 ? tierSub : allocSum;
      var sticks = Math.max(count * cfg.sticksPerPack, 1);
      return { count: count, tier: tier, base: base, oneTime: oneTime, sub: sub, subBase: subBase, allocations: allocations, sticks: sticks };
    }

    function render() {
      var t = totals();
      var single = isSinglePack();
      if (els.builder) els.builder.hidden = single;
      // Tier cards show one-time totals for that pack size (reference flavor price).
      els.tiers.forEach(function (btn) {
        // tiers in DOM are in block order; match by packs
        var packs = parseInt(btn.dataset.packs, 10);
        var tier = tiers.find(function (x) { return x.packs === packs; });
        if (!tier) return;
        var unit = refPrice();
        var total = tier.fixedAmount
          ? Math.max(Math.round(unit * packs - tier.fixedAmount * 100), 0)
          : discountedLinesTotal(new Array(packs).fill(unit), tier.oneTimeDiscount || 0);
        var priceEl = btn.querySelector('[data-byob-tier-price]');
        var perEl = btn.querySelector('[data-byob-tier-per]');
        if (priceEl) priceEl.textContent = money(total, cfg.currency);
        if (perEl) perEl.textContent = '$' + (total / 100 / (packs * cfg.sticksPerPack)).toFixed(2) + '/stick';
        var badgeEl = btn.querySelector('[data-byob-tier-badge]');
        if (badgeEl) {
          var discountPct = Math.max(tier.oneTimeDiscount || 0, tier.subscriptionDiscount || 0);
          if (badgeEl.dataset.badgeCustom === '1') {
            var customBase = (badgeEl.textContent || '').replace(/\s*Save\s+\d+%/i, '').trim();
            badgeEl.textContent = discountPct > 0 ? (customBase + ' Save ' + discountPct + '%').trim() : customBase;
            badgeEl.hidden = !badgeEl.textContent;
          } else {
            badgeEl.textContent = discountPct > 0 ? ('Save ' + discountPct + '%') : '';
            badgeEl.hidden = !badgeEl.textContent;
          }
        }
        var selected = tiers[state.tierIndex].packs === packs;
        btn.classList.toggle('is-selected', selected);
        btn.setAttribute('aria-checked', selected ? 'true' : 'false');
      });

      // Slots
      els.slots.innerHTML = '';
      els.slots.style.gridTemplateColumns = 'repeat(' + Math.min(state.slots.length, 3) + ', 1fr)';
      state.slots.forEach(function (flavorIndex, slotIdx) {
        var slot = document.createElement('div');
        slot.className = 'byob-slot' + (flavorIndex !== null ? ' is-filled' : '');
        if (flavorIndex === null) {
          slot.innerHTML =
            '<span class="byob-slot__empty" aria-hidden="true">' +
              '<span class="byob-slot__empty-icon">' +
                '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">' +
                  '<rect x="4" y="7" width="16" height="13" rx="2"/>' +
                  '<path d="M8 7V5.5A1.5 1.5 0 0 1 9.5 4h5A1.5 1.5 0 0 1 16 5.5V7"/>' +
                  '<path d="M4 12h16"/>' +
                '</svg>' +
              '</span>' +
              '<span>Empty slot</span>' +
            '</span>';
        } else {
          var f = cfg.flavors[flavorIndex];
          slot.innerHTML =
            (f.image ? '<img src="' + f.image + '" alt="">' : '') +
            '<span>' + f.name + '</span>' +
            '<button type="button" class="byob-slot__remove" aria-label="Remove ' + f.name + '">&times;</button>';
          slot.querySelector('.byob-slot__remove').addEventListener('click', function () {
            state.slots[slotIdx] = null;
            render();
          });
        }
        els.slots.appendChild(slot);
      });

      // Progress: saving grows with filled slots. Never show "Saving 0%".
      var pct = 0;
      if (t.count) pct = Math.max(t.tier.oneTimeDiscount || 0, t.tier.subscriptionDiscount || 0);
      var maxTier = tiers[tiers.length - 1];
      var maxPct = Math.max(maxTier.oneTimeDiscount || 0, maxTier.subscriptionDiscount || 0);
      if (els.progressLabel) {
        if (t.count >= maxTier.packs) {
          els.progressLabel.innerHTML = maxPct > 0
            ? ('<strong>Saving ' + maxPct + '%</strong> · ' + cfg.progressMaxText)
            : cfg.progressMaxText;
        } else {
          var next = tiers.find(function (x) { return x.packs > t.count; }) || maxTier;
          var nextPct = Math.max(next.oneTimeDiscount || 0, next.subscriptionDiscount || 0);
          var missing = Math.max(next.packs - t.count, 0);
          var hint = cfg.progressText.replace('[x]', String(missing)).replace('[y]', String(nextPct));
          if (pct > 0) {
            els.progressLabel.innerHTML = '<strong>Saving ' + pct + '%</strong> · ' + hint;
          } else if (nextPct > 0) {
            els.progressLabel.innerHTML = hint;
          } else {
            els.progressLabel.textContent = missing
              ? ('Add ' + missing + ' more flavor' + (missing === 1 ? '' : 's') + ' to fill your pack')
              : '';
          }
        }
      }
      if (els.progressBar) els.progressBar.style.width = (maxTier.packs ? (t.count / maxTier.packs) * 100 : 0) + '%';

      // Purchase options
      els.buyBtns.forEach(function (btn) {
        var selected = btn.dataset.byobBuy === state.purchase;
        btn.hidden = false;
        btn.disabled = false;
        btn.classList.toggle('is-selected', selected);
        btn.setAttribute('aria-pressed', selected ? 'true' : 'false');
      });
      if (els.subPrice) els.subPrice.textContent = t.count ? money(t.sub, cfg.currency) : '—';
      if (els.subCompare) els.subCompare.textContent = t.count && t.subBase > t.sub ? money(t.subBase, cfg.currency) : '';
      if (els.subPer) els.subPer.textContent = t.count ? '$' + (t.sub / 100 / t.sticks).toFixed(2) + '/stick' : '';
      if (els.otPrice) els.otPrice.textContent = t.count ? money(t.oneTime, cfg.currency) : '—';
      if (els.otPer) els.otPer.textContent = t.count ? '$' + (t.oneTime / 100 / t.sticks).toFixed(2) + '/stick' : '';
      // Gift strip lives inside the Subscribe card and stays visible as a fixed perk.
      if (els.subBadge) {
        // Auto badge: "SAVE <tier discount>%" unless a custom text was set in the editor.
        if (!els.subBadge.dataset.custom) {
          var badgeTier = t.count ? t.tier : tiers[state.tierIndex];
          var badgePct = Math.max(badgeTier.oneTimeDiscount || 0, badgeTier.subscriptionDiscount || 0);
          els.subBadge.textContent = badgePct ? 'Save ' + badgePct + '%' : '';
        }
        els.subBadge.style.display = els.subBadge.textContent ? '' : 'none';
      }

      var activeTotal = state.purchase === 'subscribe' ? t.sub : t.oneTime;
      if (els.atcPrice) els.atcPrice.textContent = t.count ? money(activeTotal, cfg.currency) : '';
      var pageFlavor = single && state.slots[0] != null ? cfg.flavors[state.slots[0]] : null;
      var singleBlocked = single && (!pageFlavor || !pageFlavor.available);
      if (singleBlocked) showError('Out of stock');
      else if (els.error && els.error.textContent === 'Out of stock') showError('');
      els.atc.disabled = !t.count || state.loading || singleBlocked;
    }

    els.tiers.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var packs = parseInt(btn.dataset.packs, 10);
        var idx = tiers.findIndex(function (x) { return x.packs === packs; });
        if (idx < 0) return;
        var wasSingle = isSinglePack();
        state.tierIndex = idx;
        if (packs < 2) {
          if (!wasSingle) {
            state.purchaseBeforeSingle = state.purchase;
            state.purchase = 'one_time';
          }
          var pageIdx = pageFlavorIndex();
          state.slots = [pageIdx >= 0 ? pageIdx : null];
          render();
          return;
        }
        if (wasSingle) {
          state.purchase = state.purchaseBeforeSingle || 'subscribe';
          state.purchaseBeforeSingle = null;
        }
        var kept = filled().slice(0, packs);
        state.slots = new Array(packs).fill(null);
        kept.forEach(function (f, i) { state.slots[i] = f; });
        render();
      });
    });
    els.flavors.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var flavorIndex = parseInt(btn.dataset.flavorIndex, 10);
        var empty = state.slots.indexOf(null);
        if (empty === -1) {
          showError('Your pack is full. Remove a flavor or choose a bigger pack.');
          return;
        }
        state.slots[empty] = flavorIndex;
        showError('');
        render();
        var gallery = root.querySelector('[data-byob-gallery-img]');
        var f = cfg.flavors[flavorIndex];
        if (gallery && f && f.image) gallery.src = f.image.replace('width=160', 'width=900');
      });
    });
    els.buyBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        state.purchase = btn.dataset.byobBuy;
        render();
      });
    });

    function showError(msg) {
      if (!els.error) return;
      els.error.textContent = msg || '';
      els.error.hidden = !msg;
    }

    els.atc.addEventListener('click', function () {
      var t = totals();
      if (!t.count || state.loading) return;
      if (isSinglePack()) {
        var current = state.slots[0] != null ? cfg.flavors[state.slots[0]] : null;
        if (!current || !current.available) { showError('Out of stock'); return; }
      }
      var items = filled().map(function (flavorIndex, slotIdx) {
        var f = cfg.flavors[flavorIndex];
        var properties = {
          _byob_pack_slot: String(slotIdx + 1),
          _byob_pack_size: String(t.count),
          _byob_purchase_type: state.purchase,
          _byob_subscription: state.purchase === 'subscribe' ? 'true' : 'false'
        };
        if (rule) {
          properties['_appstle-bb-id'] = rule.uniqueRef;
          properties['_appstle_bundles_type'] = rule.bundleType;
          if (rule.name) properties['__appstle-bb-name'] = rule.name;
        }
        var item = { id: f.variantId, quantity: 1, properties: properties };
        if (state.purchase === 'subscribe') {
          var allocation = t.allocations[slotIdx];
          if (allocation) item.selling_plan = allocation.id;
        }
        return item;
      });

      // Free gift: same payload (atomic), qty 1, NO selling_plan so it never recurs.
      if (state.purchase === 'subscribe' && cfg.gift && cfg.gift.variantId && cfg.gift.available) {
        if (cfg.gift.price !== 0) {
          // Misconfigured gift would charge the customer; block instead of silently charging.
          showError('Free gift is misconfigured (gift variant must be $0). Please contact support.');
          return;
        }
        items.push({
          id: cfg.gift.variantId,
          quantity: 1,
          properties: { _byob_gift: 'true', _byob_gift_for: cfg.boxName || 'Build Your Box' }
        });
      }

      state.loading = true;
      render();
      var sections = [];
      try {
        if (window.Alpine && Alpine.store('xCartHelper')) {
          sections = Alpine.store('xCartHelper').getSectionsToRender().map(function (s) { return s.id; });
        }
      } catch (e) {}

      fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: items, sections: sections })
      })
        .then(function (r) { return r.json(); })
        .then(function (response) {
          if (response.status === 422) { showError(response.description || 'Could not add to cart.'); return; }
          showError('');
          try {
            if (response.sections && window.Alpine && Alpine.store('xCartHelper')) {
              Alpine.store('xCartHelper').reRenderSections(response.sections);
            }
            if (window.Alpine && Alpine.store('xMiniCart')) {
              Alpine.store('xMiniCart').openCart();
              document.dispatchEvent(new CustomEvent('eurus:cart:redirect'));
            }
          } catch (e) {}
          document.dispatchEvent(new CustomEvent('eurus:cart:items-changed'));
          if (window.BYOBGiftSync) window.BYOBGiftSync();
        })
        .catch(function () { showError('Could not add to cart. Please try again.'); })
        .finally(function () { state.loading = false; render(); });
    });

    render();
  }

  document.querySelectorAll('[data-byob-root]').forEach(init);
  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target.querySelector && e.target.querySelector('[data-byob-root]');
    if (root) init(root);
  });
})();
